<template>
    <div>
        <h1 class="page-header">{{ $t('str.sidebar.menu.reports.events.description') }}</h1>
        <hr />

        <div v-if="!isLoading">
            <div class="row">
                <div v-if="isSuperAdminMaster" class="col-md-4 mb-3">
                    <label class="form-label" for="eventReportAccount">{{ $t('str.register.user.account.field') }}</label>
                    <select id="eventReportAccount" v-model="filters.account" class="form-select" @change="changeAccount">
                        <option value="">{{ $t('str.register.select.placeholder') }}</option>
                        <option v-for="account in accounts" :key="account._id" :value="account._id">{{ account.name }}</option>
                    </select>
                </div>

                <div v-if="['SUPER_ADMIN_MASTER', 'ADMIN', 'MANAGER'].includes(role)" class="col-md-4 mb-3">
                    <label class="form-label" for="eventReportClient">{{ $t('str.register.guard.groups.client.field') }}</label>
                    <select id="eventReportClient" v-model="filters.client" class="form-select" @change="changeClient">
                        <option value="">{{ $t('str.register.select.placeholder') }}</option>
                        <option v-for="client in clients" :key="client._id" :value="client._id">{{ client.name }}</option>
                    </select>
                </div>

                <div class="col-md-4 mb-3">
                    <label class="form-label" for="eventReportSite">{{ $t('str.register.guard.groups.site.field') }}</label>
                    <select id="eventReportSite" v-model="filters.site" class="form-select" @change="changeSite">
                        <option value="">{{ $t('str.register.select.placeholder') }}</option>
                        <option v-for="site in sites" :key="site._id" :value="site._id">{{ site.name }}</option>
                    </select>
                </div>
            </div>

            <div class="row">
                <div class="col-md-4 mb-3">
                    <label class="form-label" for="eventReportVigilant">{{ $t('str.reports.vigilant.field') }}</label>
                    <select id="eventReportVigilant" v-model="filters.vigilant" class="form-select" @change="invalidateReportResults">
                        <option value="">{{ $t('str.register.select.placeholder') }}</option>
                        <option v-for="vigilant in vigilants" :key="vigilant._id" :value="vigilant._id">{{ vigilant.fullName || vigilant.name }}</option>
                    </select>
                </div>

                <div class="col-md-4 mb-3">
                    <label class="form-label" for="eventReportTypes">{{ $t('str.reports.events.types') }}</label>
                    <v-select
                        id="eventReportTypes"
                        data-testid="event-report-types"
                        v-model="selectedTypes"
                        :options="eventTypeOptions"
                        :reduce="option => option.value"
                        label="label"
                        multiple
                        :clearable="true"
                        :searchable="false"
                        :placeholder="$t('str.register.select.placeholder')"
                        @input="invalidateReportResults"
                    />
                </div>

                <div class="col-md-4 mb-3">
                    <label class="form-label">{{ $t('str.range.date.field') }}</label>
                    <date-range-picker
                        class="form-select btn btn-dark me-2"
                        :opens="dateRange.opens"
                        :locale-data="dateRange.sampleLocaleData"
                        :singleDatePicker="dateRange.singleDatePicker"
                        :timePicker="dateRange.timePicker"
                        :timePicker24Hour="dateRange.timePicker24Hour"
                        :showWeekNumbers="dateRange.showWeekNumbers"
                        :showDropdowns="dateRange.showDropdowns"
                        :autoApply="dateRange.autoApply"
                        :linkedCalendars="dateRange.linkedCalendars"
                        :max-date="dateRange.maxDate"
                        v-model="dateRange.range"
                        @update="updateValues"
                    >
                        <template v-slot:input="range">
                            <i class="fa fa-calendar fa-fw text-white text-opacity-50 ms-n1"></i>
                            {{ formatDate(range.startDate).split(' - ')[0] }} - {{ formatDate(range.endDate).split(' - ')[0] }}
                        </template>
                    </date-range-picker>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-md-12 text-center">
                    <button type="button" class="btn btn-primary w-200px me-2 mb-1" :disabled="isSearchLoading" @click="filterFromFirstPage">
                        <i v-if="isSearchLoading" class="fas fa-spinner fa-pulse"></i>
                        {{ $t('str.generate.report') }}
                    </button>
                    <button type="button" class="btn btn-default w-200px mb-1" :disabled="isSearchLoading" @click="clearFilters">
                        {{ $t('str.clear.fields') }}
                    </button>
                </div>
            </div>

            <div v-if="loadError" data-testid="event-report-error" class="alert alert-danger" role="alert">
                {{ loadError }}
            </div>

            <div v-if="reportHeader.length" data-testid="event-report-context" class="mb-3">
                <div v-for="(line, index) in reportHeader" :key="index">{{ line }}</div>
            </div>

            <div v-show="totalItems > 0" class="d-flex gap-2 mb-3">
                <download-excel
                    class="btn btn-primary"
                    :fetch="fetchExportRows"
                    :data="[]"
                    :fields="exportFields"
                    :name="`${exportFilename}.csv`"
                    :header="exportHeader"
                    type="csv"
                    :escapeCsv="true"
                >
                    CSV
                </download-excel>
                <download-excel
                    class="btn btn-primary"
                    :fetch="fetchExportRows"
                    :data="[]"
                    :fields="exportFields"
                    :name="`${exportFilename}.xls`"
                    :header="exportHeader"
                    type="xls"
                >
                    XLS
                </download-excel>
                <button type="button" class="btn btn-primary" :disabled="isExportLoading" @click="exportPdf">
                    <i v-if="isExportLoading" class="fas fa-spinner fa-pulse"></i>
                    PDF
                </button>
            </div>

            <vue-good-table
                :columns="columns"
                :rows="items"
                :totalRows="totalItems"
                mode="remote"
                :isLoading.sync="isSearchLoading"
                :search-options="{ enabled: false }"
                :pagination-options="paginationOptions"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
            >
                <div slot="emptystate" class="vgt-center-align vgt-text-disabled">
                    <i v-if="isSearchLoading" class="fas fa-spinner fa-spin" />
                    <span v-else>{{ $t('str.table.subtitle.no.data') }}</span>
                </div>
                <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'date'">{{ formatEventDate(props.row.date) }}</span>
                    <span v-else-if="props.column.field === 'type'">{{ $t(props.row.type) }}</span>
                    <div v-else-if="props.column.field === 'attendance'" class="attendance-details">
                        <strong>{{ attendanceLabel(props.row) }}</strong>
                        <template v-if="isAttended(props.row)">
                            <small v-if="attendanceOperator(props.row)" class="d-block">
                                {{ $t('str.table.reports.column.operator') }}: {{ attendanceOperator(props.row) }}
                            </small>
                            <small v-if="props.row.attendance && props.row.attendance.openedDate" class="d-block">
                                {{ $t('str.incident.opened.date') }}: {{ formatEventDate(props.row.attendance.openedDate) }}
                            </small>
                            <small v-if="props.row.attendance && props.row.attendance.closedDate" class="d-block">
                                {{ $t('str.incident.closed.date') }}: {{ formatEventDate(props.row.attendance.closedDate) }}
                            </small>
                            <small v-for="treatment in props.row.treatments || []" :key="treatment._id" class="d-block mt-1">
                                {{ formatTreatment(treatment) }}
                            </small>
                        </template>
                    </div>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
            </vue-good-table>
        </div>

        <div v-else class="center-spinner"><i class="fas fa-spinner fa-spin" /></div>
        <notifications group="top-right" position="top right" :speed="1000" />
    </div>
</template>

<script>
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment-timezone'
import Common from '../../../common/Common.vue'
import Services from '../../../common/Services.vue'
import { applySessionScope } from '../../../utils/scope'
import { EVENT_REPORT_TYPES, formatEventReportDate, parseEventReportTypePreset, toEventReportExportRow } from './eventReport'
import { getAllEventReportRows, getEventReport } from './eventReportService'

const createDateRange = vue => {
    const startDate = moment().startOf('day')
    const endDate = moment().endOf('day')
    const today = moment()
    const yesterday = moment().subtract(1, 'day')

    return {
        opens: 'left',
        singleDatePicker: false,
        timePicker: false,
        timePicker24Hour: false,
        showWeekNumbers: false,
        showDropdowns: true,
        autoApply: true,
        linkedCalendars: true,
        maxDate: moment().format(),
        range: { startDate, endDate },
        sampleLocaleData: {
            direction: 'ltr',
            format: 'dd/mm/yyyy',
            separator: ' - ',
            applyLabel: vue.$t('str.apply'),
            cancelLabel: vue.$t('str.cancel'),
            weekLabel: 'W',
            ranges: {
                [vue.$t('str.today')]: [today, today],
                [vue.$t('str.yesterday')]: [yesterday, yesterday],
                [vue.$t('str.this_month')]: [moment().startOf('month'), today],
                [vue.$t('str.this_year')]: [moment().startOf('year'), today],
                [vue.$t('str.last_month')]: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
            },
        },
    }
}

export default {
    name: 'EventsReport',
    data() {
        const dateRange = createDateRange(this)

        return {
            accounts: [],
            clients: [],
            sites: [],
            vigilants: [],
            selectedTypes: parseEventReportTypePreset(this.$route?.query?.types),
            filters: {
                account: '',
                client: '',
                site: '',
                vigilant: '',
                startDate: dateRange.range.startDate,
                endDate: dateRange.range.endDate,
            },
            dateRange,
            role: '',
            isSuperAdminMaster: false,
            isLoading: true,
            isSearchLoading: false,
            isExportLoading: false,
            loadError: '',
            accountLoadRequestId: 0,
            reportLoadRequestId: 0,
            reportHeader: [],
            exportHeader: [],
            items: [],
            totalItems: 0,
            currentPage: 1,
            perPage: 50,
        }
    },
    computed: {
        eventTypeOptions() {
            return EVENT_REPORT_TYPES.map(value => ({ value, label: this.$t(value) }))
        },
        columns() {
            return [
                { label: this.$t('str.table.reports.column.date'), field: 'date', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
                { label: this.$t('str.table.reports.column.type'), field: 'type', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
                { label: this.$t('str.table.reports.column.account'), field: 'account', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
                { label: this.$t('str.table.reports.column.client'), field: 'client', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
                { label: this.$t('str.table.reports.column.site'), field: 'site', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
                { label: this.$t('str.table.reports.column.event'), field: 'event', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
                { label: this.$t('str.table.reports.column.attendance'), field: 'attendance', sortable: false },
            ]
        },
        exportColumns() {
            return this.columns.map(({ label, field }) => ({ label, field }))
        },
        exportFields() {
            return this.exportColumns.reduce((fields, column) => {
                fields[column.label] = column.field
                return fields
            }, {})
        },
        exportFilename() {
            return `events_report_${moment().format('YYYY-MM-DD')}`
        },
        paginationOptions() {
            return {
                enabled: true,
                mode: 'pages',
                perPage: this.perPage,
                perPageDropdown: [10, 20, 50, 100],
                dropdownAllowAll: false,
                setCurrentPage: this.currentPage,
                firstLabel: this.$t('str.table.pagination.first.page'),
                lastLabel: this.$t('str.table.pagination.last.page'),
                nextLabel: this.$t('str.table.pagination.next.page'),
                prevLabel: this.$t('str.table.pagination.prev.page'),
                rowsPerPageLabel: this.$t('str.table.pagination.rows.per.page.lavel'),
                ofLabel: this.$t('str.table.pagination.of.label.page'),
                pageLabel: this.$t('str.table.pagination.page'),
            }
        },
    },
    async mounted() {
        await this.initialize()
    },
    methods: {
        async initialize() {
            try {
                const [isSuperAdminMaster, role] = await Promise.all([Common.isSuperAdminMaster(this), Common.getSubtype(this)])
                this.isSuperAdminMaster = isSuperAdminMaster
                this.role = role || ''

                if (isSuperAdminMaster) {
                    this.accounts = await Services.getAccounts(this)
                    return
                }

                this.filters.account = Common.getAccountId(this)
                const sessionAccount = this.sessionAccountOption()
                this.accounts = sessionAccount ? [sessionAccount] : []
                const [sites, clients, vigilants] = await Promise.all([
                    Services.getSites(this),
                    ['ADMIN', 'MANAGER'].includes(this.role) ? Services.getClients(this) : Promise.resolve([]),
                    this.filters.account ? Services.getVigilantsByAccount(this, this.filters.account) : Promise.resolve([]),
                ])
                this.sites = sites
                this.clients = clients
                this.vigilants = vigilants
            } catch {
                this.loadError = this.$t('str.reports.events.load.error')
            } finally {
                this.isLoading = false
            }
        },
        reportFilters() {
            return {
                ...this.filters,
                startDate: this.companyDayBoundary(this.filters.startDate, 'startOf'),
                endDate: this.companyDayBoundary(this.filters.endDate, 'endOf'),
                types: parseEventReportTypePreset(this.selectedTypes),
            }
        },
        companyDayBoundary(value, boundary) {
            if (!value) return ''
            return moment.tz(moment(value).format('YYYY-MM-DD'), this.selectedAccountTimezone())[boundary]('day')
        },
        createReportHeader(filters, context) {
            const all = this.$t('str.event.filter.all')
            const client = this.displayEntityName(filters.client, context.clients) || all
            const site = this.displayEntityName(filters.site, context.sites) || all
            const date = value => moment(value).tz(context.timezone).format('DD/MM/YYYY')
            return [
                this.$t('str.sidebar.menu.reports.events.description'),
                `${this.$t('str.table.reports.column.client')}: ${client}`,
                `${this.$t('str.table.reports.column.site')}: ${site}`,
                `${this.$t('str.reports.events.query.date')}: ${this.formatEventDate(new Date(), context.timezone)}`,
                `${this.$t('str.range.date.field')}: ${date(filters.startDate)} - ${date(filters.endDate)}`,
            ]
        },
        hasReportScope() {
            if (!applySessionScope(this, this.filters)) return false

            if (this.isSuperAdminMaster && !this.filters.account) {
                Common.show(this, 'top-right', 'warn', this.$t('str.charts.select.account.required'))
                return false
            }

            return true
        },
        async filter() {
            if (this.isSearchLoading || !this.hasReportScope()) return

            const requestId = ++this.reportLoadRequestId
            const filters = this.reportFilters()
            const pagination = { page: this.currentPage, limit: this.perPage }
            const viewContext = this.reportViewContext()
            const reportHeader = this.createReportHeader(filters, viewContext)
            this.isSearchLoading = true
            this.loadError = ''

            try {
                const result = await getEventReport(this, filters, pagination)
                if (requestId !== this.reportLoadRequestId) return
                this.reportHeader = reportHeader
                this.items = this.applyEntityNames(result.results, viewContext)
                this.totalItems = result.totalCount
            } catch {
                if (requestId !== this.reportLoadRequestId) return
                this.items = []
                this.totalItems = 0
                this.loadError = this.$t('str.reports.events.load.error')
            } finally {
                if (requestId === this.reportLoadRequestId) this.isSearchLoading = false
            }
        },
        async filterFromFirstPage() {
            this.currentPage = 1
            await this.filter()
        },
        async onPageChange({ currentPage }) {
            this.cancelPendingReportLoad()
            this.currentPage = currentPage
            this.items = []
            this.totalItems = 0
            await this.filter()
        },
        async onPerPageChange({ currentPerPage }) {
            this.cancelPendingReportLoad()
            this.perPage = currentPerPage
            this.currentPage = 1
            this.items = []
            this.totalItems = 0
            await this.filter()
        },
        async fetchExportRows() {
            if (this.isExportLoading || !this.hasReportScope()) return []

            const filters = this.reportFilters()
            const viewContext = this.reportViewContext()
            this.exportHeader = this.reportHeader.length ? [...this.reportHeader] : this.createReportHeader(filters, viewContext)
            this.isExportLoading = true
            this.loadError = ''

            try {
                const rows = await getAllEventReportRows(this, filters)
                return this.applyEntityNames(rows, viewContext).map(row => toEventReportExportRow(row, {
                    attended: this.$t('str.reports.events.attended'),
                    notAttended: this.$t('str.reports.events.not.attended'),
                    formatDate: value => this.formatEventDate(value, viewContext.timezone),
                    translate: value => this.$t(value),
                }))
            } catch (error) {
                this.loadError = this.$t('str.reports.events.load.error')
                throw error
            } finally {
                this.isExportLoading = false
            }
        },
        async exportPdf() {
            const rows = await this.fetchExportRows()
            if (rows.length === 0) return

            const header = this.exportHeader
            const document = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
            document.setFontSize(14)
            document.text(header[0], 40, 35)
            document.setFontSize(10)
            document.text(header.slice(1), 40, 53)
            document.autoTable({
                startY: 110,
                head: [this.exportColumns.map(column => column.label)],
                body: rows.map(row => this.exportColumns.map(column => row[column.field] || '')),
                styles: { fontSize: 5 },
                theme: 'striped',
            })
            document.save(`${this.exportFilename}.pdf`)
        },
        async changeAccount() {
            this.invalidateReportResults()
            const requestId = ++this.accountLoadRequestId
            this.filters.client = ''
            this.filters.site = ''
            this.filters.vigilant = ''
            this.clients = []
            this.sites = []
            this.vigilants = []

            if (!this.filters.account) return

            const accountId = this.filters.account
            const loadCollection = async (field, loader) => {
                const values = await loader()
                if (requestId === this.accountLoadRequestId && this.filters.account === accountId) this[field] = values || []
            }
            const results = await Promise.allSettled([
                loadCollection('clients', () => Services.getClientsByAccount(this, accountId)),
                loadCollection('sites', () => Services.getSitesByAccount(this, accountId)),
                loadCollection('vigilants', () => Services.getVigilantsByAccount(this, accountId)),
            ])
            if (requestId === this.accountLoadRequestId && results.some(result => result.status === 'rejected')) {
                this.loadError = this.$t('str.reports.events.load.error')
            }
        },
        async changeClient() {
            this.invalidateReportResults()
            const requestId = ++this.accountLoadRequestId
            this.filters.site = ''
            this.filters.vigilant = ''
            this.sites = []
            this.vigilants = []

            if (!this.filters.client) return

            const clientId = this.filters.client
            const loadCollection = async (field, loader) => {
                const values = await loader()
                if (requestId === this.accountLoadRequestId && this.filters.client === clientId) this[field] = values || []
            }
            const results = await Promise.allSettled([
                loadCollection('sites', () => Services.getSitesByClient(this, clientId)),
                loadCollection('vigilants', () => Services.getVigilantsByClient(this, clientId)),
            ])
            if (requestId === this.accountLoadRequestId && results.some(result => result.status === 'rejected')) {
                this.loadError = this.$t('str.reports.events.load.error')
            }
        },
        async changeSite() {
            this.invalidateReportResults()
            const requestId = ++this.accountLoadRequestId
            this.filters.vigilant = ''
            this.vigilants = []
            if (!this.filters.site) return

            const siteId = this.filters.site
            try {
                const vigilants = await Services.getVigilantsBySite(this, siteId)
                if (requestId === this.accountLoadRequestId && this.filters.site === siteId) this.vigilants = vigilants || []
            } catch {
                if (requestId === this.accountLoadRequestId) this.loadError = this.$t('str.reports.events.load.error')
            }
        },
        clearFilters() {
            this.invalidateReportResults()
            this.accountLoadRequestId += 1
            const dateRange = createDateRange(this)
            this.dateRange = dateRange
            this.filters = {
                account: this.isSuperAdminMaster ? '' : Common.getAccountId(this),
                client: '',
                site: '',
                vigilant: '',
                startDate: dateRange.range.startDate,
                endDate: dateRange.range.endDate,
            }
            this.selectedTypes = [...EVENT_REPORT_TYPES]
            this.loadError = ''
        },
        updateValues(range) {
            this.filters.startDate = moment(range.startDate).startOf('day')
            this.filters.endDate = moment(range.endDate).endOf('day')
            this.invalidateReportResults()
        },
        invalidateReportResults() {
            this.reportHeader = []
            this.reportLoadRequestId += 1
            this.items = []
            this.totalItems = 0
            this.currentPage = 1
            this.isSearchLoading = false
            this.loadError = ''
        },
        cancelPendingReportLoad() {
            if (!this.isSearchLoading) return

            this.reportLoadRequestId += 1
            this.isSearchLoading = false
        },
        formatDate(value) {
            return value ? moment(value).format('DD/MM/YYYY') : ''
        },
        formatEventDate(value, timezone = '') {
            return formatEventReportDate(value, timezone || this.selectedAccountTimezone())
        },
        selectedAccountTimezone() {
            const selectedAccount = this.accounts.find(account => account?._id === this.filters.account)
            const timezone = selectedAccount?.timezone || this.$session.get('user')?.account?.timezone || Common.getAccountTimezone()
            return moment.tz.zone(timezone || '') ? timezone : 'America/Sao_Paulo'
        },
        reportViewContext() {
            return {
                accounts: this.accounts.map(account => ({ ...account })),
                clients: this.clients.map(client => ({ ...client })),
                sites: this.sites.map(site => ({ ...site })),
                timezone: this.selectedAccountTimezone(),
            }
        },
        isAttended(row) {
            if (typeof row?.attended === 'boolean') return row.attended
            return row?.attendance?.isAttendance === true
        },
        attendanceLabel(row) {
            return this.isAttended(row) ? this.$t('str.reports.events.attended') : this.$t('str.reports.events.not.attended')
        },
        attendanceOperator(row) {
            return this.displayEntityName(row?.attendance?.operator)
        },
        formatTreatment(treatment) {
            return [
                treatment?.type ? this.$t(treatment.type) : '',
                treatment?.notes,
                this.displayEntityName(treatment?.operator),
                this.formatEventDate(treatment?.createDate),
            ].filter(Boolean).join(' — ')
        },
        sessionAccountOption() {
            const account = this.$session.get('user')?.account
            if (!account?._id) return null
            return {
                _id: account._id,
                name: account.name || account.fantasyName || account.socialName || account._id,
                timezone: account.timezone,
            }
        },
        displayEntityName(value, options = []) {
            if (!value) return ''
            if (typeof value === 'object') return value.name || value.fantasyName || value.socialName || value.fullName || value.description || value._id || ''

            const option = options.find(item => item?._id === value || item?.id === value)
            if (option) return option.name || option.fantasyName || option.socialName || option.fullName || option.description || value

            return value
        },
        applyEntityNames(rows, context = null) {
            const viewContext = context || this.reportViewContext()
            return (rows || []).map(row => {
                const normalizedRow = { ...row }
                if (Object.prototype.hasOwnProperty.call(row, 'account')) normalizedRow.account = this.displayEntityName(row.account, viewContext.accounts)
                if (Object.prototype.hasOwnProperty.call(row, 'client')) normalizedRow.client = this.displayEntityName(row.client, viewContext.clients)
                if (Object.prototype.hasOwnProperty.call(row, 'site')) normalizedRow.site = this.displayEntityName(row.site, viewContext.sites)
                return normalizedRow
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.vue-daterange-picker.btn {
    padding: 2px !important;
    display: flex !important;
    background-color: var(--app-component-bg) !important;
    text-align-last: start !important;
}
</style>
